/**
 * Recurring Order Service
 *
 * Handles CRUD operations for recurring orders (US-RECURRING-ORDERS).
 * Recurring orders are standing orders that generate instances on scheduled dates.
 * Instances can be confirmed (creating sales), skipped, or modified.
 */

import { supabase, createSaleWithItems } from '../supabase-client'
import { generateUnifiedForecast } from './demandForecastService'
import { toLocalDateString, parseDateString } from '../utils/dateUtils'

// =============================================================================
// FORECAST REGENERATION HELPER
// =============================================================================

/**
 * Trigger demand forecast regeneration after recurring order changes
 * Runs asynchronously to not block the main operation
 */
function triggerForecastRegeneration() {
  // Run in background - don't await
  generateUnifiedForecast()
    .then(result => {
      if (result.success) {
        console.log(`[RecurringOrderService] Forecast regeneration complete: ${result.forecastsGenerated} forecasts`)
      } else {
        console.error('[RecurringOrderService] Forecast regeneration failed:', result.error)
      }
    })
    .catch(err => {
      console.error('[RecurringOrderService] Forecast regeneration error:', err)
    })
}

// =============================================================================
// PRICE LOOKUP HELPER
// =============================================================================

/**
 * Look up price for a product + product type combination
 * Price = base_price_per_kg * price_multiplier
 *
 * @param {string} productId - Product UUID
 * @param {string} productTypeId - Product type UUID
 * @returns {Promise<number>} Calculated price per kg
 */
async function lookupPrice(productId, productTypeId) {
  // Fetch product base price
  const { data: product, error: productError } = await supabase
    .from('products')
    .select('base_price_per_kg')
    .eq('id', productId)
    .single()

  if (productError || !product) {
    console.warn(`[lookupPrice] Could not find product ${productId}:`, productError)
    return 0
  }

  // Fetch product type multiplier
  const { data: productType, error: typeError } = await supabase
    .from('product_types')
    .select('price_multiplier')
    .eq('id', productTypeId)
    .single()

  if (typeError || !productType) {
    console.warn(`[lookupPrice] Could not find product type ${productTypeId}:`, typeError)
    // Return base price if no type found
    return product.base_price_per_kg || 0
  }

  const basePrice = product.base_price_per_kg || 0
  const multiplier = productType.price_multiplier || 1.0
  const calculatedPrice = Math.round(basePrice * multiplier * 100) / 100

  return calculatedPrice
}

// =============================================================================
// CREATE OPERATIONS
// =============================================================================

/**
 * Create a new recurring order with items
 *
 * @param {Object} order - Recurring order details
 * @param {string} order.customerId - Customer UUID
 * @param {string} [order.branchId] - Branch UUID (optional)
 * @param {string} [order.name] - Optional friendly name
 * @param {number[]} order.daysOfWeek - Array of day numbers (0=Sun, 1=Mon, ..., 6=Sat)
 * @param {string} [order.endType='indefinite'] - End type: 'indefinite', 'until_date', 'after_count'
 * @param {string} [order.endDate] - End date for 'until_date' type (YYYY-MM-DD)
 * @param {number} [order.maxOccurrences] - Max occurrences for 'after_count' type
 * @param {Array<Object>} items - Line items for the recurring order
 * @param {string} items[].productId - Product UUID
 * @param {string} items[].productTypeId - Product type UUID
 * @param {number} items[].quantity - Quantity in kg
 * @returns {Promise<Object>} Created recurring order with items
 */
export async function createRecurringOrder(order, items) {
  // Get current user for created_by
  const { data: { user } } = await supabase.auth.getUser()

  // Validate required fields
  if (!order.customerId) {
    throw new Error('Customer ID is required')
  }
  if (!order.daysOfWeek || order.daysOfWeek.length === 0) {
    throw new Error('At least one day of week is required')
  }
  if (!items || items.length === 0) {
    throw new Error('At least one item is required')
  }

  // Insert recurring order
  const { data: recurringOrder, error: orderError } = await supabase
    .from('recurring_orders')
    .insert([{
      customer_id: order.customerId,
      branch_id: order.branchId || null,
      name: order.name || null,
      days_of_week: order.daysOfWeek,
      end_type: order.endType || 'indefinite',
      end_date: order.endDate || null,
      max_occurrences: order.maxOccurrences || null,
      created_by: user?.id || null
    }])
    .select()
    .single()

  if (orderError) {
    console.error('[RecurringOrderService] Error creating recurring order:', orderError)
    throw orderError
  }

  // Insert items
  const itemsToInsert = items.map(item => ({
    recurring_order_id: recurringOrder.id,
    product_id: item.productId,
    product_type_id: item.productTypeId,
    quantity: item.quantity
  }))

  const { data: createdItems, error: itemsError } = await supabase
    .from('recurring_order_items')
    .insert(itemsToInsert)
    .select()

  if (itemsError) {
    console.error('[RecurringOrderService] Error creating recurring order items:', itemsError)
    // Clean up: delete the recurring order if items failed
    await supabase.from('recurring_orders').delete().eq('id', recurringOrder.id)
    throw itemsError
  }

  return {
    ...recurringOrder,
    items: createdItems
  }
}

// =============================================================================
// FETCH OPERATIONS
// =============================================================================

/**
 * Fetch recurring orders with optional filters
 *
 * @param {Object} [options] - Filter options
 * @param {string} [options.customerId] - Filter by customer
 * @param {string} [options.branchId] - Filter by branch
 * @param {boolean} [options.activeOnly=true] - Only return active orders
 * @returns {Promise<Array>} Array of recurring orders with items and customer/branch details
 */
export async function fetchRecurringOrders(options = {}) {
  const {
    customerId,
    branchId,
    activeOnly = true
  } = options

  let query = supabase
    .from('recurring_orders')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address),
      items:recurring_order_items(
        id,
        product_id,
        product_type_id,
        quantity,
        product:products(id, strain),
        product_type:product_types(id, name)
      )
    `)
    .order('created_at', { ascending: false })

  if (customerId) {
    query = query.eq('customer_id', customerId)
  }

  if (branchId) {
    query = query.eq('branch_id', branchId)
  }

  if (activeOnly) {
    query = query.eq('active', true)
  }

  const { data, error } = await query

  if (error) {
    console.error('[RecurringOrderService] Error fetching recurring orders:', error)
    throw error
  }

  return data || []
}

/**
 * Fetch a single recurring order by ID with full details
 *
 * @param {string} id - Recurring order UUID
 * @returns {Promise<Object>} Recurring order with items, customer, and branch
 */
export async function fetchRecurringOrderById(id) {
  const { data, error } = await supabase
    .from('recurring_orders')
    .select(`
      *,
      customer:customers(id, name),
      branch:branches(id, name, address),
      items:recurring_order_items(
        id,
        product_id,
        product_type_id,
        quantity,
        product:products(id, strain),
        product_type:product_types(id, name)
      )
    `)
    .eq('id', id)
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error fetching recurring order:', error)
    throw error
  }

  return data
}

// =============================================================================
// UPDATE OPERATIONS
// =============================================================================

/**
 * Update a recurring order's pattern or end conditions
 * Note: This does NOT regenerate instances (that's Step 3)
 *
 * @param {string} id - Recurring order UUID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.name] - Friendly name
 * @param {number[]} [updates.daysOfWeek] - Days of week pattern
 * @param {string} [updates.endType] - End type
 * @param {string} [updates.endDate] - End date
 * @param {number} [updates.maxOccurrences] - Max occurrences
 * @returns {Promise<Object>} Updated recurring order
 */
export async function updateRecurringOrder(id, updates) {
  const updateData = {}

  if (updates.name !== undefined) {
    updateData.name = updates.name
  }
  if (updates.daysOfWeek !== undefined) {
    if (!updates.daysOfWeek || updates.daysOfWeek.length === 0) {
      throw new Error('At least one day of week is required')
    }
    updateData.days_of_week = updates.daysOfWeek
  }
  if (updates.endType !== undefined) {
    updateData.end_type = updates.endType
  }
  if (updates.endDate !== undefined) {
    updateData.end_date = updates.endDate
  }
  if (updates.maxOccurrences !== undefined) {
    updateData.max_occurrences = updates.maxOccurrences
  }
  if (updates.branchId !== undefined) {
    updateData.branch_id = updates.branchId
  }

  if (Object.keys(updateData).length === 0) {
    throw new Error('No valid update fields provided')
  }

  const { data, error } = await supabase
    .from('recurring_orders')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error updating recurring order:', error)
    throw error
  }

  return data
}

// =============================================================================
// PAUSE/RESUME OPERATIONS
// =============================================================================

/**
 * Pause a recurring order until a specific date
 * Keeps active=true (paused is temporary)
 *
 * @param {string} id - Recurring order UUID
 * @param {string} untilDate - Date to pause until (YYYY-MM-DD)
 * @returns {Promise<Object>} Updated recurring order
 */
export async function pauseRecurringOrder(id, untilDate) {
  if (!untilDate) {
    throw new Error('Pause until date is required')
  }

  const { data, error } = await supabase
    .from('recurring_orders')
    .update({ paused_until: untilDate })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error pausing recurring order:', error)
    throw error
  }

  return data
}

/**
 * Resume a paused recurring order
 * Clears the paused_until date
 *
 * @param {string} id - Recurring order UUID
 * @returns {Promise<Object>} Updated recurring order
 */
export async function resumeRecurringOrder(id) {
  const { data, error } = await supabase
    .from('recurring_orders')
    .update({ paused_until: null })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error resuming recurring order:', error)
    throw error
  }

  return data
}

// =============================================================================
// CANCEL OPERATIONS
// =============================================================================

/**
 * Cancel a recurring order permanently
 * Sets active=false and optionally deletes pending instances
 *
 * @param {string} id - Recurring order UUID
 * @param {Object} [options] - Cancel options
 * @param {boolean} [options.deletePendingInstances=true] - Delete pending instances
 * @returns {Promise<Object>} Updated recurring order
 */
export async function cancelRecurringOrder(id, options = {}) {
  const { deletePendingInstances = true } = options

  // Optionally delete pending instances first
  if (deletePendingInstances) {
    const { error: deleteError } = await supabase
      .from('recurring_order_instances')
      .delete()
      .eq('recurring_order_id', id)
      .eq('status', 'pending')

    if (deleteError) {
      console.error('[RecurringOrderService] Error deleting pending instances:', deleteError)
      throw deleteError
    }
  }

  // Set active to false
  const { data, error } = await supabase
    .from('recurring_orders')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error cancelling recurring order:', error)
    throw error
  }

  // Trigger forecast regeneration - cancelled order removes all pending demand
  triggerForecastRegeneration()

  return data
}

// =============================================================================
// INSTANCE QUERY HELPERS
// =============================================================================

/**
 * Fetch all recurring order instances (any status) within a date range.
 * Used by the Weekly Orders popover to detect which orders are already confirmed.
 *
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of instances with id, recurring_order_id, scheduled_date, status, sale_id
 */
export async function fetchInstancesForDateRange(fromDate, toDate) {
  const { data, error } = await supabase
    .from('recurring_order_instances')
    .select('id, recurring_order_id, scheduled_date, status, sale_id')
    .gte('scheduled_date', fromDate)
    .lte('scheduled_date', toDate)

  if (error) throw error
  return data || []
}

/**
 * Ensure a recurring order instance exists for a specific date.
 * Tries to INSERT a new pending instance; if one already exists (pre-generated
 * by the 6-month forward generator, or created by a concurrent action), fetches
 * and returns the existing row instead of failing with a 23505 conflict.
 *
 * Callers must inspect the returned instance's status — it may be pending,
 * confirmed, skipped, or cancelled depending on prior actions.
 *
 * @param {string} recurringOrderId - Recurring order UUID
 * @param {string} scheduledDate - Target date (YYYY-MM-DD)
 * @returns {Promise<Object>} Created or existing instance
 */
export async function createSingleInstance(recurringOrderId, scheduledDate) {
  const { data, error } = await supabase
    .from('recurring_order_instances')
    .insert({ recurring_order_id: recurringOrderId, scheduled_date: scheduledDate, status: 'pending' })
    .select()
    .single()

  if (!error) return data

  if (error.code === '23505') {
    const { data: existing, error: fetchError } = await supabase
      .from('recurring_order_instances')
      .select('*')
      .eq('recurring_order_id', recurringOrderId)
      .eq('scheduled_date', scheduledDate)
      .single()
    if (fetchError) throw fetchError
    return existing
  }

  throw error
}

// =============================================================================
// INSTANCE OPERATIONS
// =============================================================================

/**
 * Confirm a recurring order instance and create a sale
 *
 * @param {string} instanceId - Instance UUID
 * @param {Object} [modifications] - Optional modifications to items
 * @param {Array} [modifications.items] - Modified items array
 * @param {string} [modifications.consignmentNo] - Consignment number for the sale
 * @returns {Promise<Object>} Created sale
 */
export async function confirmInstance(instanceId, modifications = {}) {
  // Fetch the instance with recurring order details
  const { data: instance, error: fetchError } = await supabase
    .from('recurring_order_instances')
    .select(`
      *,
      recurring_order:recurring_orders(
        id,
        customer_id,
        branch_id,
        items:recurring_order_items(
          id,
          product_id,
          product_type_id,
          quantity,
          product:products(id, strain),
          product_type:product_types(id, name)
        )
      )
    `)
    .eq('id', instanceId)
    .single()

  if (fetchError) {
    console.error('[RecurringOrderService] Error fetching instance:', fetchError)
    throw fetchError
  }

  if (!instance) {
    throw new Error('Instance not found')
  }

  if (instance.status !== 'pending') {
    throw new Error(`Cannot confirm instance with status: ${instance.status}`)
  }

  // Get current user for actioned_by
  const { data: { user } } = await supabase.auth.getUser()

  // Determine items to use (modified or original)
  const recurringOrder = instance.recurring_order
  let rawItems

  if (modifications.items && modifications.items.length > 0) {
    // Use modified items
    rawItems = modifications.items.map(item => ({
      product_id: item.productId || item.product_id,
      product_type_id: item.productTypeId || item.product_type_id,
      quantity: item.quantity,
      price: item.price
    }))
  } else if (instance.modified_items && instance.modified_items.length > 0) {
    // Use previously stored modified items
    rawItems = instance.modified_items.map(item => ({
      product_id: item.product_id,
      product_type_id: item.product_type_id,
      quantity: item.quantity,
      price: item.price
    }))
  } else {
    // Use original recurring order items
    rawItems = recurringOrder.items.map(item => ({
      product_id: item.product_id,
      product_type_id: item.product_type_id,
      quantity: item.quantity,
      price: item.price
    }))
  }

  // Look up prices for items that don't have them
  const itemsForSale = await Promise.all(rawItems.map(async (item) => {
    if (item.price && item.price > 0) {
      return item // Price already set
    }
    // Look up price from product base_price * product_type multiplier
    const price = await lookupPrice(item.product_id, item.product_type_id)
    return { ...item, price }
  }))

  // Create the sale using existing function
  const sale = await createSaleWithItems({
    date: instance.scheduled_date,
    customer_id: recurringOrder.customer_id,
    branch_id: recurringOrder.branch_id,
    recurring_order_id: recurringOrder.id,
    consignment_no: modifications.consignmentNo || null,
    status: 'pending',
    items: itemsForSale
  })

  // Update instance status
  const updateData = {
    status: 'confirmed',
    sale_id: sale.id,
    actioned_at: new Date().toISOString(),
    actioned_by: user?.id || null
  }

  // Store modifications if provided
  if (modifications.items && modifications.items.length > 0) {
    updateData.modified_items = modifications.items.map(item => ({
      product_id: item.productId || item.product_id,
      product_type_id: item.productTypeId || item.product_type_id,
      quantity: item.quantity
    }))
  }

  const { error: updateError } = await supabase
    .from('recurring_order_instances')
    .update(updateData)
    .eq('id', instanceId)

  if (updateError) {
    console.error('[RecurringOrderService] Error updating instance status:', updateError)
    // Note: Sale was created successfully, just logging the status update failure
  }

  // Increment occurrences_completed on the recurring order
  const { error: incrementError } = await supabase.rpc('increment_occurrences', {
    order_id: recurringOrder.id
  })

  // If RPC doesn't exist, do manual increment
  if (incrementError && incrementError.code === '42883') {
    // Function not found - do manual update
    const { data: currentOrder } = await supabase
      .from('recurring_orders')
      .select('occurrences_completed')
      .eq('id', recurringOrder.id)
      .single()

    await supabase
      .from('recurring_orders')
      .update({
        occurrences_completed: (currentOrder?.occurrences_completed || 0) + 1
      })
      .eq('id', recurringOrder.id)
  }

  // Trigger forecast regeneration - instance confirmed means demand is now a sale
  triggerForecastRegeneration()

  return sale
}

/**
 * Skip a recurring order instance
 *
 * @param {string} instanceId - Instance UUID
 * @param {string} [reason] - Optional reason for skipping
 * @returns {Promise<Object>} Updated instance
 */
export async function skipInstance(instanceId, reason = null) {
  // Verify instance exists and is pending
  const { data: instance, error: fetchError } = await supabase
    .from('recurring_order_instances')
    .select('id, status')
    .eq('id', instanceId)
    .single()

  if (fetchError) {
    console.error('[RecurringOrderService] Error fetching instance:', fetchError)
    throw fetchError
  }

  if (!instance) {
    throw new Error('Instance not found')
  }

  if (instance.status !== 'pending') {
    throw new Error(`Cannot skip instance with status: ${instance.status}`)
  }

  // Get current user for actioned_by
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('recurring_order_instances')
    .update({
      status: 'skipped',
      skip_reason: reason,
      actioned_at: new Date().toISOString(),
      actioned_by: user?.id || null
    })
    .eq('id', instanceId)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error skipping instance:', error)
    throw error
  }

  // Trigger forecast regeneration - skipped instance removes that demand
  triggerForecastRegeneration()

  return data
}

/**
 * Fetch pending instances for the suggestions panel
 *
 * @param {Object} [options] - Filter options
 * @param {string} [options.fromDate] - Start date (YYYY-MM-DD)
 * @param {string} [options.toDate] - End date (YYYY-MM-DD)
 * @param {string} [options.customerId] - Filter by customer
 * @param {string} [options.branchId] - Filter by branch
 * @param {number} [options.limit=50] - Maximum results
 * @returns {Promise<Array>} Array of pending instances with full context
 */
export async function fetchPendingInstances(options = {}) {
  const {
    fromDate,
    toDate,
    customerId,
    branchId,
    limit = 50
  } = options

  let query = supabase
    .from('recurring_order_instances')
    .select(`
      *,
      recurring_order:recurring_orders(
        id,
        name,
        customer_id,
        branch_id,
        days_of_week,
        active,
        paused_until,
        customer:customers(id, name),
        branch:branches(id, name, address),
        items:recurring_order_items(
          id,
          product_id,
          product_type_id,
          quantity,
          product:products(id, strain),
          product_type:product_types(id, name)
        )
      )
    `)
    .eq('status', 'pending')
    .order('scheduled_date', { ascending: true })
    .limit(limit)

  if (fromDate) {
    query = query.gte('scheduled_date', fromDate)
  }

  if (toDate) {
    query = query.lte('scheduled_date', toDate)
  }

  const { data, error } = await query

  if (error) {
    console.error('[RecurringOrderService] Error fetching pending instances:', error)
    throw error
  }

  // Filter by customer/branch if needed (via recurring_order relationship)
  let filteredData = data || []

  if (customerId) {
    filteredData = filteredData.filter(
      instance => instance.recurring_order?.customer_id === customerId
    )
  }

  if (branchId) {
    filteredData = filteredData.filter(
      instance => instance.recurring_order?.branch_id === branchId
    )
  }

  // Filter out instances from inactive or paused recurring orders
  const today = toLocalDateString(new Date())
  filteredData = filteredData.filter(instance => {
    const order = instance.recurring_order
    if (!order || !order.active) return false
    if (order.paused_until && order.paused_until >= today) return false
    return true
  })

  return filteredData
}

// =============================================================================
// INSTANCE GENERATION (Step 3)
// =============================================================================

/**
 * Generate recurring order instances for a specified period
 *
 * Creates instances for all matching dates within the horizon period,
 * respecting end conditions and avoiding duplicates.
 *
 * @param {string} recurringOrderId - Recurring order UUID
 * @param {number} [months=6] - Number of months to generate instances for
 * @returns {Promise<number>} Count of instances created
 */
export async function generateInstances(recurringOrderId, months = 6) {
  // Fetch the recurring order to get pattern and end conditions
  const { data: order, error: fetchError } = await supabase
    .from('recurring_orders')
    .select('*')
    .eq('id', recurringOrderId)
    .single()

  if (fetchError) {
    console.error('[RecurringOrderService] Error fetching recurring order for generation:', fetchError)
    throw fetchError
  }

  if (!order) {
    throw new Error('Recurring order not found')
  }

  // Validate days_of_week
  if (!order.days_of_week || order.days_of_week.length === 0) {
    throw new Error('Recurring order has no days of week defined')
  }

  // Check if order is active
  if (!order.active) {
    console.log('[RecurringOrderService] Skipping instance generation for inactive order:', recurringOrderId)
    return 0
  }

  // Calculate date boundaries
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Determine start date
  let startDate = new Date(today)

  // If paused_until is set and in the future, start from that date
  if (order.paused_until) {
    const pausedUntil = parseDateString(order.paused_until)
    if (pausedUntil > startDate) {
      startDate = pausedUntil
    }
  }

  // Calculate end of generation horizon
  const horizonEnd = new Date(today)
  horizonEnd.setMonth(horizonEnd.getMonth() + months)

  // Determine effective end date based on end_type
  let effectiveEndDate = new Date(horizonEnd)

  if (order.end_type === 'until_date' && order.end_date) {
    const orderEndDate = parseDateString(order.end_date)
    if (orderEndDate < effectiveEndDate) {
      effectiveEndDate = orderEndDate
    }
    // If end_date is in the past, no instances to generate
    if (orderEndDate < startDate) {
      console.log('[RecurringOrderService] End date is before start date, no instances to generate')
      return 0
    }
  }

  // Calculate max instances for after_count
  let maxToGenerate = Infinity
  if (order.end_type === 'after_count' && order.max_occurrences) {
    const remaining = order.max_occurrences - (order.occurrences_completed || 0)
    if (remaining <= 0) {
      console.log('[RecurringOrderService] Max occurrences already reached')
      return 0
    }
    maxToGenerate = remaining
  }

  // Fetch existing instances to avoid duplicates
  const { data: existingInstances, error: existingError } = await supabase
    .from('recurring_order_instances')
    .select('scheduled_date')
    .eq('recurring_order_id', recurringOrderId)

  if (existingError) {
    console.error('[RecurringOrderService] Error fetching existing instances:', existingError)
    throw existingError
  }

  // Also check for existing sales already created from this recurring order
  // (prevents duplicate instances when an instance was confirmed then deleted)
  const { data: existingSales, error: salesError } = await supabase
    .from('sales')
    .select('date')
    .eq('recurring_order_id', recurringOrderId)

  if (salesError) {
    console.error('[RecurringOrderService] Error fetching existing sales:', salesError)
    // Non-fatal — proceed with instance-only dedup
  }

  // Create Set of existing dates for O(1) lookup (instances + confirmed sales)
  const existingDates = new Set(
    (existingInstances || []).map(i => i.scheduled_date)
  )
  for (const sale of (existingSales || [])) {
    existingDates.add(sale.date)
  }

  // Generate dates that match the days_of_week pattern
  const datesToCreate = []
  const currentDate = new Date(startDate)

  while (currentDate <= effectiveEndDate && datesToCreate.length < maxToGenerate) {
    // Convert JS day (0=Sun) to ISO day (1=Mon, 7=Sun)
    const jsDay = currentDate.getDay()
    const isoDay = jsDay === 0 ? 7 : jsDay

    // Check if this day matches the pattern
    if (order.days_of_week.includes(isoDay)) {
      const dateStr = formatDateStr(currentDate)

      // Only add if not already existing
      if (!existingDates.has(dateStr)) {
        datesToCreate.push(dateStr)
        existingDates.add(dateStr) // Prevent duplicates within this batch
      }
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1)
  }

  // If no dates to create, return early
  if (datesToCreate.length === 0) {
    console.log('[RecurringOrderService] No new instances to create')
    return 0
  }

  // Batch insert instances
  const instances = datesToCreate.map(date => ({
    recurring_order_id: recurringOrderId,
    scheduled_date: date,
    status: 'pending'
  }))

  const { error: insertError } = await supabase
    .from('recurring_order_instances')
    .insert(instances)

  if (insertError) {
    console.error('[RecurringOrderService] Error inserting instances:', insertError)
    throw insertError
  }

  console.log(`[RecurringOrderService] Created ${instances.length} instances for order ${recurringOrderId}`)

  // Trigger forecast regeneration for the new instances
  triggerForecastRegeneration()

  return instances.length
}

/**
 * Regenerate all pending instances for a recurring order
 *
 * Deletes all pending instances and creates fresh ones.
 * Used after editing a recurring order pattern.
 *
 * @param {string} recurringOrderId - Recurring order UUID
 * @param {number} [months=6] - Number of months to generate instances for
 * @returns {Promise<{deleted: number, created: number}>} Counts of deleted and created instances
 */
export async function regenerateInstances(recurringOrderId, months = 6) {
  // Count pending instances before deletion for reporting
  const { count: pendingCount, error: countError } = await supabase
    .from('recurring_order_instances')
    .select('*', { count: 'exact', head: true })
    .eq('recurring_order_id', recurringOrderId)
    .eq('status', 'pending')

  if (countError) {
    console.error('[RecurringOrderService] Error counting pending instances:', countError)
    throw countError
  }

  // Delete all pending instances
  const { error: deleteError } = await supabase
    .from('recurring_order_instances')
    .delete()
    .eq('recurring_order_id', recurringOrderId)
    .eq('status', 'pending')

  if (deleteError) {
    console.error('[RecurringOrderService] Error deleting pending instances:', deleteError)
    throw deleteError
  }

  console.log(`[RecurringOrderService] Deleted ${pendingCount || 0} pending instances for order ${recurringOrderId}`)

  // Generate fresh instances
  const created = await generateInstances(recurringOrderId, months)

  return {
    deleted: pendingCount || 0,
    created
  }
}

/**
 * Extend instances for a recurring order beyond existing ones
 *
 * Generates additional instances beyond the current horizon.
 * Used to extend the planning horizon as time passes.
 *
 * @param {string} recurringOrderId - Recurring order UUID
 * @param {number} [additionalMonths=6] - Additional months to extend by
 * @returns {Promise<number>} Count of new instances created
 */
export async function extendInstances(recurringOrderId, additionalMonths = 6) {
  // Find the latest scheduled date for this order
  const { data: latestInstance, error: fetchError } = await supabase
    .from('recurring_order_instances')
    .select('scheduled_date')
    .eq('recurring_order_id', recurringOrderId)
    .order('scheduled_date', { ascending: false })
    .limit(1)
    .single()

  if (fetchError && fetchError.code !== 'PGRST116') {
    // PGRST116 = no rows found, which is okay
    console.error('[RecurringOrderService] Error fetching latest instance:', fetchError)
    throw fetchError
  }

  // Calculate the new horizon based on latest instance or today
  let baseDate
  if (latestInstance) {
    baseDate = parseDateString(latestInstance.scheduled_date)
  } else {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    baseDate = now
  }

  // Temporarily modify the order's generation start point
  // by calling generateInstances with a longer horizon
  const totalMonths = additionalMonths + (latestInstance ? 0 : 6)

  const created = await generateInstances(recurringOrderId, totalMonths)

  console.log(`[RecurringOrderService] Extended instances for order ${recurringOrderId}: ${created} new instances`)
  return created
}

/**
 * Helper: Format a date as YYYY-MM-DD string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
function formatDateStr(date) {
  return toLocalDateString(date)
}

// =============================================================================
// COMMITMENT THRESHOLD OPERATIONS (US-013E)
// =============================================================================

/**
 * Get the commitment threshold from system config
 * Recurring order instances within this threshold automatically become pending sales
 *
 * @returns {Promise<number>} Threshold in days (default 56 = 8 weeks)
 */
export async function getCommitmentThreshold() {
  const { data, error } = await supabase
    .from('system_config')
    .select('commitment_threshold_days')
    .single()

  if (error) {
    console.warn('[RecurringOrderService] Error fetching commitment threshold, using default:', error)
    return 56
  }

  return data?.commitment_threshold_days ?? 56
}

/**
 * Commit all pending recurring order instances within the threshold
 * Creates pending sales and marks instances as committed
 *
 * This function is called by the daily Edge Function to automatically
 * create sales for recurring orders that have crossed the 8-week threshold
 *
 * @returns {Promise<{committed: number, errors: Array}>} Results summary
 */
export async function commitPendingInstances() {
  const results = { committed: 0, errors: [] }

  try {
    // 1. Get commitment threshold from system config
    const thresholdDays = await getCommitmentThreshold()
    const thresholdDate = new Date()
    thresholdDate.setDate(thresholdDate.getDate() + thresholdDays)
    const thresholdDateStr = formatDateStr(thresholdDate)

    console.log(`[RecurringOrderService] Committing instances up to ${thresholdDateStr} (${thresholdDays} days)`)

    // 2. Find pending instances within threshold that aren't committed
    const { data: instances, error: fetchError } = await supabase
      .from('recurring_order_instances')
      .select(`
        *,
        recurring_order:recurring_orders(
          id,
          customer_id,
          branch_id,
          items:recurring_order_items(
            product_id,
            product_type_id,
            quantity,
            product:products(id, strain),
            product_type:product_types(id, name)
          )
        )
      `)
      .eq('status', 'pending')
      .is('committed_at', null)
      .lte('scheduled_date', thresholdDateStr)

    if (fetchError) {
      console.error('[RecurringOrderService] Error fetching pending instances:', fetchError)
      results.errors.push({ error: 'Failed to fetch pending instances', details: fetchError.message })
      return results
    }

    if (!instances || instances.length === 0) {
      console.log('[RecurringOrderService] No instances to commit')
      return results
    }

    console.log(`[RecurringOrderService] Found ${instances.length} instances to commit`)

    // 3. For each instance, create pending sale and mark committed
    for (const instance of instances) {
      try {
        const recurringOrder = instance.recurring_order

        if (!recurringOrder || !recurringOrder.items || recurringOrder.items.length === 0) {
          throw new Error('Instance has no recurring order or items')
        }

        // Prepare items for sale creation
        // Note: Prices are applied at commitment time (current prices from product_types)
        const itemsForSale = recurringOrder.items.map(item => ({
          product_id: item.product_id,
          product_type_id: item.product_type_id,
          quantity: item.quantity,
          // Price will be looked up by createSaleWithItems from product_types
        }))

        // Create sale with auto-commit flags
        const sale = await createSaleWithItems({
          date: instance.scheduled_date,
          customer_id: recurringOrder.customer_id,
          branch_id: recurringOrder.branch_id,
          status: 'pending',
          items: itemsForSale,
          // Mark as auto-committed recurring sale
          is_auto_committed: true,
          recurring_order_id: recurringOrder.id
        })

        console.log(`[RecurringOrderService] Created sale ${sale.id} for instance ${instance.id}`)

        // Mark instance as committed with timestamp and sale link
        const { error: updateError } = await supabase
          .from('recurring_order_instances')
          .update({
            committed_at: new Date().toISOString(),
            sale_id: sale.id
          })
          .eq('id', instance.id)

        if (updateError) {
          console.error('[RecurringOrderService] Error marking instance as committed:', updateError)
          // Sale was created but instance wasn't marked - log but continue
          results.errors.push({
            instanceId: instance.id,
            saleId: sale.id,
            error: 'Failed to mark instance as committed',
            details: updateError.message
          })
        } else {
          results.committed++
          console.log(`[RecurringOrderService] Marked instance ${instance.id} as committed`)
        }

      } catch (error) {
        console.error(`[RecurringOrderService] Error committing instance ${instance.id}:`, error)
        results.errors.push({
          instanceId: instance.id,
          scheduledDate: instance.scheduled_date,
          error: error.message
        })
      }
    }

    console.log(`[RecurringOrderService] Commitment complete: ${results.committed} committed, ${results.errors.length} errors`)

  } catch (error) {
    console.error('[RecurringOrderService] Fatal error in commitPendingInstances:', error)
    results.errors.push({ error: 'Fatal error', details: error.message })
  }

  return results
}

// =============================================================================
// ITEM OPERATIONS
// =============================================================================

/**
 * Update items for a recurring order
 * Replaces all existing items with new items
 *
 * @param {string} recurringOrderId - Recurring order UUID
 * @param {Array<Object>} items - New items
 * @param {string} items[].productId - Product UUID
 * @param {string} items[].productTypeId - Product type UUID
 * @param {number} items[].quantity - Quantity in kg
 * @returns {Promise<Array>} Updated items
 */
export async function updateRecurringOrderItems(recurringOrderId, items) {
  if (!items || items.length === 0) {
    throw new Error('At least one item is required')
  }

  // Delete existing items
  const { error: deleteError } = await supabase
    .from('recurring_order_items')
    .delete()
    .eq('recurring_order_id', recurringOrderId)

  if (deleteError) {
    console.error('[RecurringOrderService] Error deleting existing items:', deleteError)
    throw deleteError
  }

  // Insert new items
  const itemsToInsert = items.map(item => ({
    recurring_order_id: recurringOrderId,
    product_id: item.productId,
    product_type_id: item.productTypeId,
    quantity: item.quantity
  }))

  const { data, error } = await supabase
    .from('recurring_order_items')
    .insert(itemsToInsert)
    .select()

  if (error) {
    console.error('[RecurringOrderService] Error inserting new items:', error)
    throw error
  }

  // Trigger forecast regeneration - item changes affect future demand
  triggerForecastRegeneration()

  return data || []
}

/**
 * Update modified_items for a specific recurring order instance
 * Used for "this order only" changes that shouldn't affect the template
 *
 * @param {string} instanceId - Instance UUID
 * @param {Array<Object>} items - Modified items
 * @param {string} items[].productId - Product UUID
 * @param {string} items[].productTypeId - Product type UUID
 * @param {number} items[].quantity - Quantity in kg
 * @returns {Promise<Object>} Updated instance
 */
export async function updateInstanceItems(instanceId, items) {
  if (!items || items.length === 0) {
    throw new Error('At least one item is required')
  }

  // Format items for the JSONB column
  const modifiedItems = items.map(item => ({
    product_id: item.productId,
    product_type_id: item.productTypeId,
    quantity: item.quantity
  }))

  const { data, error } = await supabase
    .from('recurring_order_instances')
    .update({ modified_items: modifiedItems })
    .eq('id', instanceId)
    .select()
    .single()

  if (error) {
    console.error('[RecurringOrderService] Error updating instance items:', error)
    throw error
  }

  console.log(`[RecurringOrderService] Updated instance ${instanceId} with modified items`)
  return data
}
